/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Message, Modal, Select, Space, Table, TableColumnProps, Typography } from '@arco-design/web-react';
import { get } from '../requestNew';
import dayjs from 'dayjs';
import PhoneCell from '../components/PhoneCell';
import { Item } from './types';
import './index.scss';

const Option = Select.Option;


/** 支付状态：空字符串表示全部，请求时不传 orderStatus */
const orderStatusFilterOptions = [
  { label: '全部', value: '' },
  { label: '支付成功', value: 'SUCCESS' },
  { label: '未支付', value: 'NOTPAY' },
  { label: '转入退款', value: 'REFUND' },
  { label: '已关闭', value: 'CLOSED' },
  { label: '已撤销（付款码）', value: 'REVOKED' },
  { label: '用户支付中（付款码）', value: 'USERPAYING' },
  { label: '支付失败（付款码）', value: 'PAYERROR' }
];

const orderStatusLabelByValue: Record<string, string> = Object.fromEntries(
  orderStatusFilterOptions.filter(o => o.value !== '').map(({ value, label }) => [value, label])
);

/** 是否删除：空字符串表示全部，请求时不传 deleted；否则传布尔 */
const deletedFilterOptions = [
  { label: '全部', value: '' },
  { label: '未删除', value: 'false' },
  { label: '已删除', value: 'true' }
];

/** 与「是否删除」列展示规则一致：已逻辑删除不可再删 */
function isRecordLogicDeleted(record: Item): boolean {
  const v = record?.isValid;
  if (v === false || v === 0) return true;
  if (typeof v === 'string' && v.trim() === '0') return true;
  return false;
}

/** 与 toRequestParams 的 f 字段一致；用于在 setState 尚未提交时立刻按新筛选项请求 */
type GetListFilterOverrides = Partial<{
  gameName: string;
  phone: string;
  openId: string;
  orderId: string;
  gameSession: string;
  orderStatus: string;
  deleted: string;
}>;

function PayActivity() {
  const [loading, setLoading] = useState(false);
  const [gameName, setGameName] = useState('');
  const [phone, setPhone] = useState('');
  const [openId, setOpenId] = useState('');
  const [orderId, setOrderId] = useState('');
  const [gameSession, setGameSession] = useState('event_2026_98'); // 默认选中复盘交流群
  const [orderStatusFilter, setOrderStatusFilter] = useState('');
  const [deletedFilter, setDeletedFilter] = useState('');
  const [dataList, setDataList] = useState<Item[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const isInitialMount = useRef(true);

  // 下拉选项配置
  const gameSessionOptions = [
  { label: '复盘交流群', value: 'event_2026_98' },
    { label: '核心群', value: 'event_2025_599' },
    { label: '516线下活动', value: 'event_offline_2026_516' },
    { label: '711线下活动', value: 'event_offline_2026_711' },
    { label: '912线下活动', value: 'event_offline_2026_912' },
  ];

  /** 组装列表查询参数：支付状态、是否删除为「全部」时不传对应入参 */
  const toRequestParams = (
    page: number,
    f: {
      gameName: string;
      phone: string;
      openId: string;
      orderId: string;
      gameSession: string;
      orderStatus: string;
      deleted: string;
    }
  ) => {
    const params: Record<string, string | number | boolean> = {
      pageNum: page,
      pageSize,
      gameName: f.gameName,
      phone: f.phone,
      gameSession: f.gameSession
    };
    const openIdTrim = f.openId?.trim();
    if (openIdTrim) {
      params.openId = openIdTrim;
    }
    const orderIdTrim = f.orderId?.trim();
    if (orderIdTrim) {
      params.orderId = orderIdTrim;
    }
    if (f.orderStatus) {
      params.orderStatus = f.orderStatus;
    }
    if (f.deleted === 'true') {
      params.deleted = true;
    } else if (f.deleted === 'false') {
      params.deleted = false;
    }
    return params;
  };

  const handleLogicDelete = (record: Item) => {
    if (isRecordLogicDeleted(record)) {
      return;
    }
    if (record?.id == null) {
      Message.error('记录 ID 不存在');
      return;
    }
    Modal.confirm({
      title: '提示',
      content: '确定逻辑删除该用户吗？',
      focusLock: true,
      onOk: () =>
        get(`/sys/umsUserQualifications/logicDelUserById`, { id: record.id }).then(() => {
          Message.success('删除成功');
          getList(false, false);
        })
    });
  };

  const columns: TableColumnProps[] = [
    {
      title: 'openId',
      dataIndex: 'openId',
      width: 200
    },
    {
      title: '参赛名',
      dataIndex: 'gameName',
      width: 140
    },
    {
      title: '报名时间',
      dataIndex: 'createTime',
      width: 120,
      render: text => dayjs(text).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: '支付时间',
      dataIndex: 'payTime',
      width: 120,
      render: (text, record: Item) =>
        record?.orderStatus === 'SUCCESS' && text != null && text !== '' && dayjs(text).isValid()
          ? dayjs(text).format('YYYY-MM-DD HH:mm:ss')
          : '-'
    },
    {
      title: '订单号',
      dataIndex: 'orderId',
      width: 120
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      width: 160,
      render: text => <PhoneCell phone={text} />
    },
    {
      title: '支付状态',
      dataIndex: 'orderStatus',
      width: 160,
      render: text => {
        const status = text == null || text === '' ? '' : String(text);
        if (!status) return '-';
        const label = orderStatusLabelByValue[status] ?? status;
        const color =
          status === 'SUCCESS' ? '#52c41a' : status === 'USERPAYING' ? '#faad14' : undefined;
        return <span style={color ? { color } : undefined}>{label}</span>;
      }
    },
    {
      title: '支付金额',
      dataIndex: 'payPrice',
      width: 100,
      render: text =>
        text != null && text !== ''
          ? `¥${(Number(text) / 100).toFixed(2)}`
          : '-'
    },
    {
      title: '是否删除',
      dataIndex: 'isValid',
      width: 100,
      render: (_text, record: Item) => {
        const v = record?.isValid;
        if (v === true || v === 1) {
          return <span style={{ color: '#52c41a' }}>未删除</span>;
        }
        if (v === false || v === 0) {
          return <span style={{ color: 'var(--color-text-3)' }}>已删除</span>;
        }
        if (typeof v === 'string') {
          const t = v.trim();
          if (t === '1') return <span style={{ color: '#52c41a' }}>未删除</span>;
          if (t === '0') return <span style={{ color: 'var(--color-text-3)' }}>已删除</span>;
        }
        return '-';
      }
    },
    {
      title: '操作',
      dataIndex: 'operation',
      width: 88,
      fixed: 'right',
      render: (_text, record: Item) =>
        record.orderStatus === 'SUCCESS' ? (
          <Button
            type="text"
            status="danger"
            size="small"
            disabled={isRecordLogicDeleted(record)}
            onClick={() => handleLogicDelete(record)}
          >
            删除
          </Button>
        ) : (
          ''
        )
    }
  ];

  const nickNameChange = (val: string) => {
    setGameName(val);
  };

  const handleReset = () => {
    setGameName('');
    setPhone('');
    setOpenId('');
    setOrderId('');
    setGameSession('event_2026_98'); // 重置为默认值
    setOrderStatusFilter('');
    setDeletedFilter('');
    setPageNum(1);
    // 重置后重新获取列表，使用空值作为参数
    setDataList([]);
    setLoading(true);
    const params = toRequestParams(1, {
      gameName: '',
      phone: '',
      openId: '',
      orderId: '',
      gameSession: 'event_2026_98',
      orderStatus: '',
      deleted: ''
    });
    get(`/sys/umsUserQualifications/getUserListByEvent`, params)
      .then((res: any) => {
        const { code, data, message } = res || {};
        if (code === 200) {
          setDataList(data.records || []);
          setTotal(data.total || 0);
        } else {
          Message.error(message);
        }
      })
      .catch(e => {
        Message.error('网络错误!');
        console.log(e);
      })
      .finally(() => setLoading(false));
  };


  const getList = (needLoading = false, resetPage = false, filterOverrides?: GetListFilterOverrides) => {
    if (needLoading) {
      setDataList([]);
      setLoading(true);
    }

    const currentPageNum = resetPage ? 1 : pageNum;
    if (resetPage) {
      setPageNum(1);
    }

    const params = toRequestParams(currentPageNum, {
      gameName: filterOverrides?.gameName ?? gameName,
      phone: filterOverrides?.phone ?? phone,
      openId: filterOverrides?.openId ?? openId,
      orderId: filterOverrides?.orderId ?? orderId,
      gameSession: filterOverrides?.gameSession ?? gameSession,
      orderStatus: filterOverrides?.orderStatus ?? orderStatusFilter,
      deleted: filterOverrides?.deleted ?? deletedFilter
    });

    get(`/sys/umsUserQualifications/getUserListByEvent`,  params)
      .then((res: any) => {
        const { code, data, message } = res || {};
        if (code === 200) {
          setDataList(data.records || []);
          setTotal(data.total || 0);
        } else {
          Message.error(message);
        }
      })
      .catch(e => {
        Message.error('网络错误!');
        console.log(e);
      })
      .finally(() => setLoading(false));
  };


  useEffect(() => {
    // 避免初始加载时重复请求
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (pageNum > 0 && pageSize > 0) {
      getList(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNum, pageSize]);

  // 页面初始化加载
  useEffect(() => {
    getList(false, true);
  }, []);

  return (
    <div className="pay-activity table-flex">
      <Space style={{ flexWrap: 'wrap' }}>
        <div className="filter-item">
          <Typography.Text>参赛名:</Typography.Text>
          <Input value={gameName} onChange={nickNameChange} style={{ width: 190 }} allowClear />
        </div>
        <div className="filter-item">
          <Typography.Text>手机号:</Typography.Text>
          <Input value={phone} onChange={setPhone} style={{ width: 190 }} allowClear />
        </div>
        <div className="filter-item">
          <Typography.Text>openId:</Typography.Text>
          <Input value={openId} onChange={setOpenId} style={{ width: 190 }} allowClear placeholder="选填" />
        </div>
        <div className="filter-item">
          <Typography.Text>订单号:</Typography.Text>
          <Input value={orderId} onChange={setOrderId} style={{ width: 190 }} allowClear placeholder="请输入订单号" />
        </div>
        <div className="filter-item">
          <Typography.Text>群组:</Typography.Text>
          <Select 
            value={gameSession} 
            onChange={(value) => {
              setGameSession(value);
              getList(false, true, { gameSession: value });
            }} 
            style={{ width: 190 }} 
          >
            {gameSessionOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </div>
        <div className="filter-item">
          <Typography.Text>支付状态:</Typography.Text>
          <Select
            value={orderStatusFilter}
            onChange={value => {
              setOrderStatusFilter(value);
              getList(false, true, { orderStatus: value });
            }}
            style={{ width: 190 }}
            allowClear={false}
          >
            {orderStatusFilterOptions.map(option => (
              <Option key={option.label + option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </div>
        <div className="filter-item">
          <Typography.Text>是否删除:</Typography.Text>
          <Select
            value={deletedFilter}
            onChange={value => {
              setDeletedFilter(value);
              getList(false, true, { deleted: value });
            }}
            style={{ width: 140 }}
            allowClear={false}
          >
            {deletedFilterOptions.map(option => (
              <Option key={option.label + option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </div>
        <Button
          className="filter-item"
          type="primary"
          onClick={() => {
            getList(false, true);
          }}
        >
          查询
        </Button>
        <Button
          className="filter-item"
          type="outline"
          onClick={handleReset}
        >
          重置
        </Button>
      </Space>
      <div className="table-content">
        <Table
          loading={loading}
          columns={columns}
          data={dataList}
          pagination={{
            current: pageNum,
            pageSize,
            total,
            sizeCanChange: true,
            sizeOptions: [50, 100, 200],
            showTotal: true,
            onChange: (page, size) => {
              setPageNum(page);
              if (size && size !== pageSize) {
                setPageSize(size);
              }
            }
          }}
          rowKey="id"
          className="table-scroll"
          scroll={{ y: '100%', x: '100%' }}
        />
      </div>
    </div>
  );
}

export default PayActivity;
